import { Component, computed, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { environment } from 'environments/environment';
import { WrapUpDialogComponent } from 'ng2-app/call-display/call-display/wrap-up-dialog/wrap-up-dialog.component';
import { CallUpdates } from 'ng2-app/models/dto/call.model';
import { SoftphoneService } from 'ng2-app/services/call/softphone.service';
import { RemoveActivityById } from 'ng2-app/store/activity/activity.actions';
import { CancelCall, UpdateCall, UpdateCallById } from 'ng2-app/store/call/call.actions';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import {filter, switchMap, take, takeUntil} from 'rxjs/operators';
import {BorrowerState} from 'ng2-app/store/borrower/borrower/borrower.state';
import {map, Observable} from 'rxjs';

@Component({
  selector: 'activity-row-call',
  template: `
    <div class="activity-phone">
      <div *ngIf="activity.meta.callType === 'outgoing'" style="float: left; margin-right: 10px;">
        <mat-icon>call_made</mat-icon>
      </div>
      <div *ngIf="activity.meta.callType === 'incoming'" style="float: left; margin-right: 10px;">
        <mat-icon>call_received</mat-icon>
      </div>
      <div style="margin-bottom: 1%;">
        <div class="description">
          <strong>{{ activity.meta.callType.charAt(0).toUpperCase() + activity.meta.callType.slice(1) }} Phone Call&nbsp;</strong>
          <div class="text">
            <span *ngIf="activity.meta.callType === 'outgoing'">
              {{ activity.meta.caller }} called
              <span *ngIf="activity.meta?.contactFullName">
                {{ activity.meta.contactFullName }}
              </span>
              {{ activity.meta.toNumber | tel }} - {{ activity.borrowerName }}.
              <span *ngIf="!activity.meta.disposition && (activity.meta.duration || activity.meta.connectionTime)">
                  Call connected.
              </span>
              <span
                (click)="updateCall()"
                    [matTooltip]="callTagTooltip()"
                    [ngClass]="{'tw-cursor-pointer tw-rounded tw-border-0 hover:tw-bg-lendio-warm-gray-50': canChangeCallTags()}">
                <span class='tw-pl-1' *ngIf="activity.meta.disposition && (activity.meta.duration || activity.meta.connectionTime)">
                  {{ activity.meta.disposition }}
                </span>
              </span>
              <span class='tw-pl-1' *ngIf="activity.meta.connectionTime">(connected for {{ activity.meta.connectionTime | timer }}).</span>
              <span class='tw-pl-1' *ngIf="!activity.meta.connectionTime && activity.meta.duration">
                (duration of {{ activity.meta.duration | timer }}).
              </span>
              <span class='tw-pl-1' *ngIf="activity.meta.callTag">Call Tag: {{ activity.meta.callTag }}</span>
            </span>
            <span *ngIf="activity.meta.callType === 'incoming'">
              from {{ activity.meta.caller }} - {{ activity.meta.fromNumber | tel }} to {{ activity.meta.toNumber | tel }}.
              <span *ngIf="activity.meta.duration">Call connected. (duration of {{ activity.meta.duration | timer }}).
              <span *ngIf="activity.meta.callTag">Call Tag: {{ activity.meta.callTag }}</span></span>
            </span>
          </div>
          <div class="tw-ml-8">
            <span *ngIf="activity.meta.recordingUrl && activity.meta.callId">
              <a
                [href]="url"
                target="_blank">
                <mat-icon>play_circle_outline</mat-icon>
                Listen
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [],
  standalone: false
})

export class ActivityRowCallComponent implements OnInit {
  canChangeCallTags = toSignal( this.store.select( LenderUserState.hasPermission('internal:canChangeCallTags') ) );
  callTagTooltip = computed(() => {
    const canChangeCallTags = this.canChangeCallTags();
    return canChangeCallTags ? 'Change call disposition' : '';
  });

  @Input() activity;
  url: string;

  constructor(
    private softphoneService: SoftphoneService,
    private dialog: MatDialog,
    private store: Store,
  ) {}

  ngOnInit(): void {
    const callId = this.activity.meta?.callId ?? null;
    if (callId) {
      this.url = `${environment.API_URL}/phone/recording-media/${callId}`;
    }
  }

  updateCall() {
    if (!this.canChangeCallTags()) return;

    this.softphoneService.getCall(this.activity.meta?.callId).pipe(
      take(1),
      switchMap(call => this.fetchBorrowerForCall(call).pipe(
        map(borrower => {
          if (borrower) {
            call.borrower = borrower;
          }
          return call;
        })
      ))
    ).subscribe(call => this.openWrapUpDialog(call));
  }

  private fetchBorrowerForCall(call): Observable<any> {
    return this.store.select(BorrowerState.borrower).pipe(
      filter(borrower => !!borrower && borrower.id === call.borrowerId),
      take(1)
    );
  }

  private openWrapUpDialog(call): void {
    const dialogRef = this.dialog.open(WrapUpDialogComponent, {
      disableClose: false,
      data: {
        activeCall: call,
        checkEndCall: false,
        unendedCall: false,
        allowCloseWrapUpDialog: true
      },
      panelClass: 'wrapUpDialogContainer',
      backdropClass: 'wrapUpBackdrop',
      position: { top: '150px' }
    });

    dialogRef.componentInstance.updateEndedCall.pipe(take(1)).subscribe((updates: CallUpdates) => {
      const callTag = updates.callTag || null;
      this.store.dispatch(new UpdateCallById(call.id, { ...updates, callTag }));
      this.store.dispatch(new RemoveActivityById(this.activity.id));
      dialogRef.close();
    });
  }
}
